import React, { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import isNil from 'lodash/isNil';
import Button, { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import FileIcon from '@atlaskit/icon/glyph/file';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { Box, Flex, xcss, Text, Stack } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type Props = {
	uploadText: string;
	acceptType: string;
	fileSizeLimit: number;
	fileName?: string | null;
	isFileUploading?: boolean | null;
	hasErrorOccured?: boolean | null;
	onReceiveFile: (file: File) => void;
	onCancel: () => void;
	formatError?: string | null;
	fileNotUploadederror?: string | null;
};

export const UploadButton = ({
	uploadText,
	acceptType,
	onReceiveFile,
	onCancel,
	fileSizeLimit,
	fileName,
	isFileUploading,
	hasErrorOccured = false,
	formatError = null,
	fileNotUploadederror = null,
}: Props) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [showSizeError, setShowSizeError] = useState(false);
	const [name, setName] = useState(fileName);
	const { formatMessage } = useIntl();

	const onClick = useCallback(() => {
		inputRef.current?.click();
	}, []);

	useEffect(() => {
		setName(fileName);
	}, [fileName]);

	const onChangeHandler = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			if (!isNil(event.target.files)) {
				setName(event.target.files[0].name);
				if (event.target.files[0].size <= fileSizeLimit) {
					onReceiveFile(event.target.files[0]);
				} else {
					setShowSizeError(true);
				}
			}
			// Reset file input after sending file to parent
			const inputEl = event.target;
			inputEl.value = '';
		},
		[fileSizeLimit, onReceiveFile],
	);

	const onCancelClick = useCallback(() => {
		setName(null);
		onCancel();
		setShowSizeError(false);
	}, [onCancel]);

	const uploadStatus = useMemo(() => {
		if (formatError) {
			return (
				<>
					<ErrorIcon size="small" label="" primaryColor={token('color.icon.danger')} />
					<Text size="small" color="color.text.danger">
						{formatError}
					</Text>
				</>
			);
		}
		if (showSizeError) {
			return (
				<>
					<ErrorIcon size="small" label="" primaryColor={token('color.icon.danger')} />
					<Text size="small" color="color.text.danger">
						{formatMessage(messages.sizeError, { size: fileSizeLimit / 1000000 })}
					</Text>
				</>
			);
		}
		if (isFileUploading) {
			return (
				<>
					<Spinner size="small" />
					<Text size="small" color="color.text.subtle">
						{formatMessage(messages.uploading)}
					</Text>
				</>
			);
		}
		return (
			<>
				<EditorSuccessIcon size="small" label="" primaryColor={token('color.icon.success')} />
				<Text size="small">{formatMessage(messages.uploaded)}</Text>
			</>
		);
	}, [fileSizeLimit, formatError, formatMessage, isFileUploading, showSizeError]);

	if (!hasErrorOccured && name) {
		return (
			<Flex xcss={cardWrapperStyles}>
				<Box xcss={fileIconStyles}>
					<FileIcon size="medium" label="" />
				</Box>
				<Flex xcss={fileUploadStyles}>
					<Stack space="space.050">
						<Tooltip
							content={name}
							testId="project-create-importer-upload-flow.common.ui.upload-button.name-tool-tip"
						>
							<Box xcss={fileNameStyles}>{name}</Box>
						</Tooltip>
						<Flex gap="space.050">{uploadStatus}</Flex>
					</Stack>
					<IconButton
						appearance="subtle"
						icon={EditorCloseIcon}
						onClick={onCancelClick}
						label={formatMessage(messages.close)}
					/>
				</Flex>
			</Flex>
		);
	}

	return (
		<Box>
			<Box
				xcss={inputStyles}
				as="input"
				type="file"
				accept={acceptType}
				ref={inputRef}
				onChange={onChangeHandler}
				testId="project-create-importer-upload-flow.common.ui.upload-button.box"
			/>
			<Button iconBefore={UploadIcon} onClick={onClick}>
				{uploadText}
			</Button>
			{(hasErrorOccured || Boolean(fileNotUploadederror)) && (
				<Flex xcss={errorStyles} gap="space.025">
					<ErrorIcon size="small" label="" primaryColor={token('color.icon.danger')} />
					<Text size="small" color="color.text.danger">
						{hasErrorOccured ? formatMessage(messages.failed) : fileNotUploadederror}
					</Text>
				</Flex>
			)}
		</Box>
	);
};

const inputStyles = xcss({
	visibility: 'hidden',
	position: 'absolute',
});

const cardWrapperStyles = xcss({
	padding: 'space.200',
	borderRadius: '5px',
	background: 'electation.surface',
	boxShadow: 'elevation.shadow.raised',
	maxWidth: '350px',
	gap: 'space.200',
});

const fileIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '36px',
	height: '36px',
	borderRadius: '3px',
	backgroundColor: 'color.background.accent.gray.subtlest',
});

const fileUploadStyles = xcss({
	flexGrow: 1,
	justifyContent: 'space-between',
	alignItems: 'center',
});

const fileNameStyles = xcss({
	width: '226px',
	color: 'color.text',
	font: token('font.heading.xsmall'),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const errorStyles = xcss({
	paddingBlockStart: 'space.150',
});
